import React, { useEffect, useRef } from 'react';

import { useFilters } from '@/contexts/FilterContext';
import { useSession } from '@/hooks/authHooks';
import { useNewsFeed } from '@/hooks/useNewsFeed';
import { useSubscription } from '@/hooks/useSubscription';

import { AIAlertPromoCard } from './AIAlertPromoCard';
import { NewsGridSkeleton } from './CardSkeleton';
import { NewsCard } from './NewsCard';

/**
 * Returns positions where promo cards should appear.
 * Schedule: after 6 cards, then 12 more (18), then 16 more (34), then every 16 after.
 */
function getPromoPositions(articleCount: number): number[] {
  const positions: number[] = [];
  const schedule = [5, 15, 31]; // After 6, then +12, then +16

  for (const pos of schedule) {
    if (pos <= articleCount) positions.push(pos);
  }

  // Then every 16 after position 34
  let nextPos = 34 + 16;
  while (nextPos <= articleCount) {
    positions.push(nextPos);
    nextPos += 16;
  }

  return positions;
}

export function NewsFeed() {
  const {
    selectedSymbols,
    selectedSources,
    dateRange,
    searchQuery,
    hasSymbolOnly,
  } = useFilters();

  const { session, user } = useSession();
  const isAuthenticated = Boolean(session ?? user);
  const { hasPaidTier } = useSubscription({ enabled: isAuthenticated });

  // Don't show promo cards to authenticated users on pro/premium plans
  const shouldShowPromo = !(isAuthenticated && hasPaidTier);

  const { articles, fetchNextPage, hasMore, isLoading, isFetchingNextPage } =
    useNewsFeed({
      filters: {
        symbols: selectedSymbols,
        sources: selectedSources,
        dateRange,
        searchQuery,
        hasSymbolOnly,
      },
    });

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && !isFetchingNextPage && hasMore) {
        fetchNextPage();
      }
    });
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [isFetchingNextPage, hasMore, fetchNextPage]);

  if (isLoading && articles.length === 0) {
    return <NewsGridSkeleton />;
  }

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4">
        {articles.length > 0 ? (
          (() => {
            const promoPositions = getPromoPositions(articles.length);
            const items: React.ReactNode[] = [];
            let promoIndex = 0;

            articles.forEach((article, index) => {
              items.push(<NewsCard key={article.id} article={article} />);

              // Check if we should insert a promo card after this article
              const articlePosition = index + 1;
              if (shouldShowPromo && promoPositions.includes(articlePosition)) {
                items.push(
                  <AIAlertPromoCard
                    key={`promo-${articlePosition}`}
                    position={promoIndex}
                  />
                );
                promoIndex++;
              }
            });

            return items;
          })()
        ) : (
          <div className="flex min-h-[300px] w-full items-center justify-center bg-neutral-50">
            <p className="rounded-lg bg-neutral-50 px-8 py-4 font-medium text-neutral-600">
              Niciun articol de știri găsit. Încercați să ajustați filtrele.
            </p>
          </div>
        )}
      </div>

      {articles.length > 0 && (
        <>
          <div ref={sentinelRef} className="h-4" />
          {isFetchingNextPage && (
            <div className="mt-4 flex justify-center">
              <p className="text-sm text-neutral-500">Se încarcă...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
